import { type ChangeEvent, type DragEvent, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, formatPercent } from '@/shared/lib';
import { GLYPH } from '../glyphs';
import { IconDocument } from '../icons';

/** Файл, уже сохранённый в записи: ссылка для превью/открытия и имя (если сервер его отдаёт). */
export interface UploadedFile {
  url: string | null;
  name?: string | null;
}

export interface UploadDropzoneProps {
  id: string;
  accept: string;
  title: string;
  note: ReactNode;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
  onFile: (file: File) => void;
  /** Выбор нескольких файлов (галерея): `onFile` вызывается для каждого по порядку. */
  multiple?: boolean;
}

/**
 * Зона выбора файла (прототип, поле `media`): padding 12 14, рамка 1px dashed line-field, radius 16,
 * фон paper-base, hover — surface-sunken; квадрат «+» 34 (radius 16, рамка line-strong, белый),
 * заголовок 13/500 ink-900, подпись 11 ink-500. Принимает и перетаскивание файла.
 */
export function UploadDropzone({
  id,
  accept,
  title,
  note,
  disabled = false,
  invalid = false,
  describedBy,
  onFile,
  multiple = false,
}: UploadDropzoneProps) {
  const [dragOver, setDragOver] = useState(false);

  function pick(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    // Сброс значения: повторный выбор того же файла тоже запускает загрузку.
    event.target.value = '';
    for (const file of multiple ? files : files.slice(0, 1)) onFile(file);
  }

  function drop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const files = Array.from(event.dataTransfer.files);
    for (const file of multiple ? files : files.slice(0, 1)) onFile(file);
  }

  return (
    <label
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={drop}
      className={cn(
        'flex items-center gap-12 rounded-16 border border-dashed border-line-field bg-paper-base px-14 py-12',
        'transition-colors duration-(--duration-fast) focus-within:border-wine-bordeaux focus-within:shadow-focus-field',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-surface-sunken',
        dragOver ? 'bg-surface-sunken' : undefined,
        invalid ? 'border-status-error' : undefined,
      )}
    >
      <span
        aria-hidden="true"
        className="flex size-34 shrink-0 items-center justify-center rounded-16 border border-line-strong bg-paper-pure text-[16px] text-ink-600"
      >
        {GLYPH.plus}
      </span>
      <span className="flex min-w-0 flex-col gap-2">
        <span className="text-[13px] font-medium text-ink-900">{title}</span>
        <span className="text-[11px] text-pretty text-ink-500">{note}</span>
      </span>
      <input
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={pick}
        className="sr-only"
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
      />
    </label>
  );
}

export interface UploadFileRowProps {
  kind: 'image' | 'document';
  name: string;
  /** Превью изображения (локальное или ссылка сервера). */
  previewUrl: string | null;
  /** Подпись-чип справа от имени: размер, «текущий файл» или процент загрузки. */
  chip: string;
  /** Загрузка идёт: чип золотой, под строкой — полоса прогресса. */
  progress?: number | null;
  actionLabel: string;
  onAction: () => void;
  actionDisabled?: boolean;
  /** Открыть изображение в полном размере (лайтбокс). */
  onOpen?: () => void;
  /** Ошибка загрузки этого файла (галерея). */
  error?: string | null;
  /** Строка внутри общего списка (без своей рамки, с разделителем снизу). */
  bare?: boolean;
}

/**
 * Строка файла (прототип, список файлов поля `media`): padding 7 10, миниатюра 28 (radius 9,
 * фон surface-sunken, рамка line-hair), имя 12 ink-900, чип 10/500 radius 9, «Убрать» 11 status-error.
 * Полоса прогресса — 3 px на line-soft, заполнение wine-bordeaux.
 */
export function UploadFileRow({
  kind,
  name,
  previewUrl,
  chip,
  progress = null,
  actionLabel,
  onAction,
  actionDisabled = false,
  onOpen,
  error = null,
  bare = false,
}: UploadFileRowProps) {
  const { t, i18n } = useTranslation('form');
  const uploading = progress !== null;
  const thumb = (
    <span className="relative flex size-28 shrink-0 items-center justify-center overflow-hidden rounded-9 border border-line-hair bg-surface-sunken text-ink-400">
      {kind === 'image' && previewUrl ? (
        <img src={previewUrl} alt="" className="absolute inset-0 size-full object-cover" />
      ) : kind === 'document' ? (
        <IconDocument size={14} />
      ) : (
        <span aria-hidden="true" className="text-[12px]">
          {GLYPH.square}
        </span>
      )}
    </span>
  );
  return (
    <div
      className={
        bare
          ? 'border-b border-line-soft last:border-b-0'
          : 'overflow-hidden rounded-16 border border-line-strong'
      }
    >
      <div className="flex flex-col gap-6 px-10 py-7">
        <div className="flex items-center gap-10">
          {onOpen ? (
            <button
              type="button"
              onClick={onOpen}
              title={t('upload.openFull')}
              aria-label={t('upload.openFull')}
              className="cursor-zoom-in rounded-9"
            >
              {thumb}
            </button>
          ) : (
            thumb
          )}
          <span className="min-w-0 flex-1 truncate text-[12px] text-ink-900">{name}</span>
          <span
            className={cn(
              'shrink-0 rounded-9 px-6 py-2 text-[10px] font-medium tabular',
              uploading ? 'bg-gold-wash text-gold-leaf-3' : 'bg-surface-sunken text-ink-500',
            )}
          >
            {chip}
          </span>
          <button
            type="button"
            onClick={onAction}
            disabled={actionDisabled}
            className="shrink-0 cursor-pointer text-[11px] text-status-error disabled:cursor-not-allowed disabled:opacity-50"
          >
            {actionLabel}
          </button>
        </div>
        {uploading ? (
          <div
            role="progressbar"
            aria-label={t('upload.progressLabel', { name })}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            aria-valuetext={formatPercent(progress, i18n.language)}
            className="ml-38 h-3 overflow-hidden rounded-pill bg-line-soft"
          >
            <div
              className="h-full rounded-pill bg-wine-bordeaux transition-[width] duration-(--duration-fast)"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
        ) : null}
        {error ? (
          <span role="alert" className="ml-38 text-[11px] leading-[16px] text-status-error">
            {error}
          </span>
        ) : null}
      </div>
    </div>
  );
}
